import { stringify } from 'qs'
import { Context } from 'koa'

import { PREFETCH_ORDERS_AMOUNT } from 'views/consts/orders'

import {
  OrderResponse,
  OrderResponseV1,
  OrdersResponse,
  OrdersResponseV1
} from 'shared/services/api/models/orders'

import BackendAPIService from './BackendAPIService'
import { ServiceParameters } from './commonTypes/ServiceParameters'

export interface FetchOrdersListParams {
  page: number
  itemsPerPage: number
}

const defaultOrdersListParams: FetchOrdersListParams = {
  page: 0,
  itemsPerPage: PREFETCH_ORDERS_AMOUNT
}

interface IOrdersService {
  getOrdersListURL: (params: FetchOrdersListParams) => string
  getOrderDetailsURL: (orderId: string) => string
  getOrdersController: (params: object) => string
  fetchOrdersList: (
    ctx: Context,
    params: FetchOrdersListParams
  ) => Promise<OrdersResponse>
  fetchOrder: (ctx: Context, orderId: string) => Promise<OrderResponse>
  // fetchAllCustomerOrders is used by checkout
  fetchAllCustomerOrders: (
    page: number,
    itemsPerPage: number
  ) => Promise<object>
}

class OrdersService extends BackendAPIService implements IOrdersService {
  constructor(params: ServiceParameters) {
    super({ apiVersion: 'v1', ...params })
  }

  public getOrdersListURL(
    params: FetchOrdersListParams = defaultOrdersListParams
  ): string {
    return `/account/v2/${this.tenantPath}/${this.language}/orders?${stringify(
      params
    )}`
  }

  public getOrderDetailsURL(orderId): string {
    return `/account/v2/${this.tenantPath}/${this.language}/orders/${orderId}`
  }

  public getOrdersController(params: FetchOrdersListParams): string {
    return `/order/${this.apiVersion}/${this.tenantPath}/orders/?${stringify(
      params
    )}`
  }

  public async fetchOrdersList(
    ctx: Context,
    params: FetchOrdersListParams = defaultOrdersListParams
  ): Promise<OrdersResponse> {
    this.logger.debug(
      `Orders service is fetching orders list data!`,
      'dataFetching'
    )
    const { data }: { data: OrdersResponseV1 } = await this.get(
      this.getOrdersListURL(params),
      null,
      ctx
    )
    this.logger.debug(
      `Orders service fetched orders list data!`,
      'dataFetching'
    )
    return new OrdersResponse(data)
  }

  public async fetchOrder(
    ctx: Context,
    orderId: string
  ): Promise<OrderResponse> {
    this.logger.debug(
      `Orders service is fetching single order data!`,
      'dataFetching'
    )
    const { data }: { data: OrderResponseV1 } = await this.get(
      this.getOrderDetailsURL(orderId),
      null,
      ctx
    )
    return new OrderResponse(data)
  }

  public fetchAllCustomerOrders(page = 0, itemsPerPage = 1): Promise<object> {
    return this.get(
      this.getOrdersController({
        page,
        itemsPerPage
      })
    )
  }
}

export default OrdersService
